import {
  Cable,
  CalendarClock,
  CircleUserRound,
  Download,
  DraftingCompass,
  FolderKanban,
  FolderOpen,
  Gauge,
  Gem,
  Home,
  Images,
  LifeBuoy,
  MessageSquare,
  MessagesSquare,
  Package,
  PenSquare,
  Settings,
  Share2,
  Trash2,
  Users,
  UsersRound,
  SwatchBook,
} from 'lucide-react';
import type { GlobalCommandMenuCopy } from './global-command-menu-copy';
import type { GoToContext } from './hooks/use-go-to-context';
import type { GoToItem } from './types';
import { workspaceFilesAvatarListHref } from '@/lib/workspace-avatar-files-route';
import { workspaceFilesMediaHref } from '@/lib/workspace-files-media';
import { workspaceFilesHref } from '@/lib/workspace-files-route';

/** Destinations reachable from the sidebar, the Drive rail and the workspace
 * menu. Pinned rows show with an empty query; the rest surface on search. */
function workspaceDestinations(
  orgSlug: string,
  text: GlobalCommandMenuCopy,
  context: GoToContext | undefined
): GoToItem[] {
  const items: GoToItem[] = [
    {
      id: 'home',
      label: text.home,
      href: `/${orgSlug}`,
      icon: Home,
      shortcutId: 'nav.home',
      keywords: ['tasks', 'home', 'start', 'dashboard'],
      pinned: true,
    },
    {
      id: 'products',
      label: text.products,
      href: `/${orgSlug}/products`,
      icon: Package,
      shortcutId: 'nav.products',
      keywords: ['products'],
      pinned: true,
    },
    {
      id: 'threads',
      label: text.allTasks,
      href: `/${orgSlug}/threads`,
      icon: MessageSquare,
      shortcutId: 'nav.threads',
      keywords: ['tasks', 'threads', 'chats', 'conversations'],
    },
    {
      id: 'library',
      label: context?.labels.mediaLibrary ?? text.library,
      href: workspaceFilesMediaHref(orgSlug),
      icon: Images,
      shortcutId: 'nav.library',
      keywords: ['library', 'assets', 'media', 'images', 'videos'],
      pinned: true,
    },
    {
      id: 'avatars',
      label: text.avatars,
      href: workspaceFilesAvatarListHref(orgSlug),
      icon: CircleUserRound,
      shortcutId: 'nav.avatars',
      keywords: ['avatars', 'models', 'characters'],
      pinned: true,
    },
    {
      id: 'aesthetics',
      label: text.aesthetics,
      href: `/${orgSlug}/aesthetics`,
      icon: SwatchBook,
      keywords: ['aesthetics', 'styles', 'moodboards'],
    },
    {
      id: 'members',
      label: text.members,
      href: `/${orgSlug}/settings/members`,
      icon: Users,
      keywords: ['members', 'team', 'people', 'invite'],
    },
    {
      id: 'settings',
      label: text.settings,
      href: `/${orgSlug}/settings`,
      icon: Settings,
      shortcutId: 'nav.settings',
      keywords: ['settings', 'preferences', 'configuration'],
    },
  ];
  if (!context) return items;
  const { labels } = context;
  const drivePath = (path: string) => workspaceFilesHref(orgSlug, path);
  items.push(
    {
      id: 'new-chat',
      label: labels.newChat,
      href: `/${orgSlug}/design`,
      icon: PenSquare,
      keywords: ['new chat', 'chat', 'ask', 'agent', 'design'],
      pinned: true,
    },
    {
      id: 'my-drive',
      label: labels.myDrive,
      href: context.myDriveHref,
      icon: FolderOpen,
      keywords: ['drive', 'files', 'folders', 'documents'],
      pinned: true,
    },
    {
      id: 'work-chats',
      label: labels.workChats,
      href: `/${orgSlug}/chats`,
      icon: MessagesSquare,
      keywords: ['connect', 'work chats', 'channels', 'messages', 'team'],
      pinned: true,
    },
    {
      id: 'scheduled',
      label: labels.schedule,
      href: `/${orgSlug}/scheduled`,
      icon: CalendarClock,
      keywords: ['schedule', 'scheduled', 'tasks', 'recurring', 'automation'],
      pinned: true,
    },
    {
      id: 'connectors',
      label: labels.connectors,
      href: `/${orgSlug}/connectors`,
      icon: Cable,
      keywords: ['connectors', 'integrations', 'apps', 'gmail', 'slack'],
    },
    {
      id: 'brands-folder',
      label: labels.brands,
      href: drivePath(`/${orgSlug}/brands`),
      icon: DraftingCompass,
      keywords: ['brands', 'brand', 'branding', 'identity'],
    },
    {
      id: 'products-folder',
      label: `${labels.myDrive}: ${labels.productsFolder}`,
      href: `/${orgSlug}/files/products`,
      icon: Package,
      keywords: ['products folder', 'drive', 'files'],
    },
    {
      id: 'avatars-folder',
      label: `${labels.myDrive}: ${labels.avatarsFolder}`,
      href: `/${orgSlug}/files/avatars`,
      icon: CircleUserRound,
      keywords: ['avatars folder', 'drive', 'files'],
    },
    {
      id: 'shared-with-me',
      label: labels.sharedWithMe,
      href: `/${orgSlug}/drives/shared-with-me`,
      icon: Share2,
      keywords: ['shared', 'shared with me', 'drive'],
    },
    {
      id: 'team-drives',
      label: labels.teams,
      href: `/${orgSlug}/drives/teams`,
      icon: UsersRound,
      keywords: ['teams', 'team drives', 'drives'],
    },
    {
      id: 'project-drives',
      label: labels.projects,
      href: `/${orgSlug}/drives/projects`,
      icon: FolderKanban,
      keywords: ['projects', 'project drives', 'drives'],
    },
    {
      id: 'downloads',
      label: labels.downloads,
      href: `/${orgSlug}/downloads`,
      icon: Download,
      keywords: ['downloads', 'zip', 'export'],
    },
    {
      id: 'trash',
      label: labels.trash,
      href: `/${orgSlug}/trash`,
      icon: Trash2,
      keywords: ['trash', 'deleted', 'restore', 'bin'],
    },
    {
      id: 'pricing',
      label: labels.pricing,
      href: `/${orgSlug}/settings/purchase`,
      icon: Gem,
      keywords: ['pricing', 'plans', 'upgrade', 'credits', 'purchase'],
    },
    {
      id: 'usage',
      label: labels.usage,
      href: `/${orgSlug}/settings/usage`,
      icon: Gauge,
      keywords: ['usage', 'credits', 'consumption'],
    },
    {
      id: 'support',
      label: labels.support,
      href: `/${orgSlug}/support`,
      icon: LifeBuoy,
      keywords: ['support', 'help', 'contact', 'feedback'],
    },
    ...context.settings.map((entry) => ({
      id: entry.id,
      label: `${text.settings}: ${entry.label}`,
      href: entry.href,
      icon: Settings,
      keywords: entry.keywords,
    }))
  );
  return items;
}

const PINNED_ORDER = [
  'home',
  'new-chat',
  'my-drive',
  'work-chats',
  'scheduled',
  'products',
  'library',
  'avatars',
];

export function getGlobalCommandMenuGoToItems(args: {
  orgSlug?: string;
  term: string;
  text: GlobalCommandMenuCopy;
  context?: GoToContext;
}): GoToItem[] {
  if (!args.orgSlug) return [];
  const all = workspaceDestinations(args.orgSlug, args.text, args.context);
  const lower = args.term.trim().toLowerCase();
  if (!lower) {
    return all
      .filter((item) => item.pinned)
      .sort(
        (left, right) =>
          PINNED_ORDER.indexOf(left.id) - PINNED_ORDER.indexOf(right.id)
      );
  }
  return all.filter(
    (item) =>
      item.label.toLowerCase().includes(lower) ||
      item.keywords?.some((keyword) => keyword.includes(lower))
  );
}
